import tinycolor from 'tinycolor2';
import numeral from 'numeral';
import { format, formatDistanceToNow } from 'date-fns';
import type { FpProduct } from '@/types/product.types';

export function darken(hex: string, amount: number): string {
  return tinycolor(hex).darken(amount).toHexString();
}

export function lighten(hex: string, amount: number): string {
  return tinycolor(hex).lighten(amount).toHexString();
}

export function formatCurrency(amount: number, currency = 'NGN'): string {
  return `${currency} ${numeral(amount).format('1,0.00')}`;
}

export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '—';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '—';
  return format(d, 'dd MMM yyyy');
}

export function formatDateTime(date: string | Date | null | undefined): string {
  if (!date) return '—';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '—';
  return format(d, 'dd MMM yyyy HH:mm');
}

export function formatRelativeTime(date: string | Date | null | undefined): string {
  if (!date) return '—';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '—';
  return formatDistanceToNow(d, { addSuffix: true });
}

export function formatPercent(value: number): string {
  return `${numeral(value).format('0.0')}%`;
}

export function formatNumber(value: number): string {
  return numeral(value).format('1,0');
}

export function evaluateCondition(
  fieldValue: unknown,
  operator: string,
  conditionValue: unknown,
): boolean {
  if (fieldValue === undefined || fieldValue === null) return false;
  switch (operator) {
    case 'eq':
      return fieldValue == conditionValue;
    case 'gt':
      return Number(fieldValue) > Number(conditionValue);
    case 'lt':
      return Number(fieldValue) < Number(conditionValue);
    case 'gte':
      return Number(fieldValue) >= Number(conditionValue);
    case 'lte':
      return Number(fieldValue) <= Number(conditionValue);
    case 'in':
      return Array.isArray(conditionValue) && conditionValue.includes(fieldValue);
    default:
      return false;
  }
}

export function calculatePremium(
  product: FpProduct,
  coverType: string,
  customFieldValues: Record<string, unknown>,
  sumInsured = 0,
): number {
  const ct = product.cover_types.find((c) => c.name === coverType);
  let amount = ct
    ? ct.base_premium_type === 'percentage'
      ? (ct.base_premium / 100) * sumInsured
      : ct.base_premium
    : 0;
  for (const rule of product.premium_rules ?? []) {
    const fieldValue = customFieldValues[rule.condition_field];
    const conditionMet = evaluateCondition(
      fieldValue,
      rule.operator,
      rule.condition_value,
    );
    if (conditionMet) {
      if (rule.adjustment_type === 'flat') {
        amount += rule.adjustment_value;
      } else if (rule.adjustment_type === 'percentage') {
        amount += (amount * rule.adjustment_value) / 100;
      }
    }
  }
  return Math.round(amount * 100) / 100;
}
